import type { AssetStatus } from '../api/types';

export const formatDate = (iso: string | null | undefined): string => {
  if (!iso) return '–';
  const [y, m, d] = iso.slice(0, 10).split('-');
  return y && m && d ? `${d}.${m}.${y}` : iso;
};

export const formatDateTime = (iso: string | null | undefined): string => {
  if (!iso) return '–';
  return new Intl.DateTimeFormat('de-DE', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'Europe/Berlin' }).format(new Date(iso));
};

export const formatMoney = (cents: number | null | undefined, currency = 'EUR'): string => {
  if (cents === null || cents === undefined) return '–';
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency }).format(cents / 100);
};

/** "1.349,00" oder "1349.5" → Cent; leer → null; ungültig → NaN */
export const parseMoney = (input: string): number | null => {
  const t = input.trim().replace(/\s|€/g, '');
  if (!t) return null;
  const normalized = t.includes(',') ? t.replace(/\./g, '').replace(',', '.') : t;
  const n = Number(normalized);
  return Number.isFinite(n) ? Math.round(n * 100) : NaN;
};

export const centsToInput = (cents: number | null | undefined): string =>
  cents === null || cents === undefined ? '' : (cents / 100).toFixed(2).replace('.', ',');

/** Regelsteuersatz für die automatische Netto/Brutto-Berechnung im Formular. */
export const VAT_PERCENT = 19;

/**
 * Gegenwert zu einem eingegebenen Netto- bzw. Bruttobetrag (19 % USt.).
 * Leere oder ungültige Eingabe → '' (Gegenfeld wird dann geleert, falls es automatisch befüllt war).
 */
export const counterAmount = (changed: 'net' | 'gross', input: string): string => {
  const cents = parseMoney(input);
  if (cents === null || Number.isNaN(cents)) return '';
  return centsToInput(
    changed === 'net' ? Math.round((cents * (100 + VAT_PERCENT)) / 100) : Math.round((cents * 100) / (100 + VAT_PERCENT)),
  );
};

export const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1).replace('.', ',')} MB`;
};

export const todayIso = () =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Berlin', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());

export const STATUS_LABELS: Record<AssetStatus, string> = {
  active: 'Aktiv',
  in_stock: 'Im Lager',
  repair: 'In Reparatur',
  retired: 'Ausgemustert',
  disposed: 'Verschrottet',
  lost: 'Verloren',
  unknown: 'Unbekannt',
};

export const STATUS_ORDER: AssetStatus[] = ['active', 'in_stock', 'repair', 'retired', 'lost', 'unknown', 'disposed'];

/** Erlaubte Statuswechsel (wie im Backend; "disposed" nur über die Verschrottung). */
export const STATUS_TRANSITIONS: Record<AssetStatus, AssetStatus[]> = {
  active: ['in_stock', 'repair', 'retired', 'lost', 'unknown'],
  in_stock: ['active', 'repair', 'retired', 'lost', 'unknown'],
  repair: ['active', 'in_stock', 'retired', 'lost', 'unknown'],
  retired: ['active', 'in_stock', 'lost', 'unknown'],
  lost: ['active', 'in_stock', 'retired', 'unknown'],
  unknown: ['active', 'in_stock', 'repair', 'retired', 'lost'],
  disposed: [],
};

export const CATEGORY_LABELS: Record<string, string> = {
  client: 'Arbeitsplatz',
  server: 'Server',
  display: 'Bildschirm',
  mobile: 'Mobil',
  printer: 'Drucker',
  network: 'Netzwerk',
  storage: 'Speicher',
  component: 'Komponente',
  peripheral: 'Peripherie',
  license: 'Lizenz',
  other: 'Sonstiges',
};

export const METHOD_LABELS: Record<string, string> = {
  recycling: 'Recycling',
  certified_data_destruction: 'Zertifizierte Datenvernichtung und Entsorgung',
  return_to_vendor: 'Rückgabe an Hersteller/Händler',
  sale: 'Verkauf',
  donation: 'Spende',
  other: 'Sonstiges',
};

export const CHILD_ACTION_LABELS: Record<string, string> = {
  dispose: 'Mitverschrotten',
  remove_to_stock: 'Ausbauen → Lager',
  move_to_other_asset: 'In anderes Gerät umbauen',
  keep_as_independent_asset: 'Ausbauen, eigenständig weiterführen',
};

export const DOCUMENT_CATEGORY_LABELS: Record<string, string> = {
  invoice: 'Rechnung',
  receipt: 'Kaufbeleg',
  warranty: 'Garantie',
  maintenance: 'Wartungsprotokoll',
  disposal_protocol: 'Verschrottungsprotokoll',
  disposal_certificate: 'Entsorgungsnachweis',
  other: 'Sonstiges',
};

export const PHOTO_CATEGORY_LABELS: Record<string, string> = {
  overview: 'Gesamtansicht',
  nameplate: 'Typenschild',
  serial_number: 'Seriennummer',
  condition: 'Zustand',
  other: 'Sonstiges',
};

export const ROLE_LABELS: Record<string, string> = { admin: 'Administrator', it: 'IT', read_only: 'Nur lesen' };

export const EVENT_LABELS: Record<string, string> = {
  asset_created: 'Angelegt',
  asset_updated: 'Stammdaten geändert',
  specs_changed: 'Technische Daten geändert',
  status_changed: 'Status geändert',
  employee_assigned: 'Mitarbeiter zugeordnet',
  employee_unassigned: 'Zurückgegeben',
  location_changed: 'Standort geändert',
  component_installed: 'Eingebaut',
  component_removed: 'Ausgebaut',
  component_moved: 'Umgebaut',
  child_removed: 'Komponente entfernt',
  part_added: 'Komponente hinzugefügt',
  part_updated: 'Komponente geändert',
  part_removed: 'Komponente ausgebaut',
  document_uploaded: 'Dokument hochgeladen',
  photo_uploaded: 'Foto hochgeladen',
  primary_photo_changed: 'Hauptfoto geändert',
  attachment_updated: 'Datei geändert',
  attachment_deleted: 'Datei gelöscht',
  asset_disposed: 'Verschrottet',
  asset_deleted: 'Als Fehlerfassung gelöscht',
  tax_record_created: 'Steuerwerte erfasst',
  tax_record_updated: 'Steuerwerte geändert',
  tax_record_deleted: 'Steuerwerte gelöscht',
  disposal_reverted: 'Verschrottung zurückgenommen',
};

export const FIELD_LABELS: Record<string, string> = {
  name: 'Bezeichnung',
  photo_not_required: 'Kein Foto nötig',
  exclude_from_reconciliation: 'Nicht im Inventarabgleich',
  description: 'Beschreibung',
  manufacturer: 'Hersteller',
  model: 'Modell',
  serial_number: 'Seriennummer',
  manufacturer_part_number: 'Teilenummer',
  service_tag: 'Service Tag',
  purchase_date: 'Kaufdatum',
  capitalization_date: 'AHK-Datum',
  commissioning_date: 'Inbetriebnahme',
  cost_net_cents: 'Kosten netto',
  cost_gross_cents: 'Kosten brutto',
  currency: 'Währung',
  supplier: 'Lieferant',
  invoice_number: 'Rechnungsnummer',
  order_number: 'Bestellnummer',
  warranty_until: 'Garantie bis',
  accounting_asset_number: 'Buchhaltungsnummer',
  notes: 'Notizen',
  status: 'Status',
  employee_id: 'Mitarbeiter',
  location_id: 'Standort',
  parent_asset_id: 'Eingebaut in',
  asset_type_id: 'Asset-Typ',
  tracking_type: 'Art',
};
